/*
 * Назначение: страница «Безопасность» админки.
 * Функционал: собирает три блока — настройки защиты входа, подтверждение входа
 * кодом для себя, журнал входов и доверенные устройства. Данные тянутся с
 * сервера, здесь только состояние экрана.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "../../../lib/api";
import { securityApi, type SecuritySettings } from "../../../lib/security-api";
import { useAuth } from "../../components/auth/auth-provider";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";

import { LoginAttemptsCard } from "./login-attempts";
import { SecuritySettingsCard } from "./security-settings";
import { TrustedDevicesCard } from "./trusted-devices";

export function SecurityPage() {
  const queryClient = useQueryClient();
  const { user, setUser } = useAuth();
  const [hours, setHours] = useState(48);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const settings = useQuery({
    queryKey: ["security-settings"],
    queryFn: () => securityApi.getSettings(),
  });
  const attempts = useQuery({
    queryKey: ["security-attempts", hours],
    queryFn: () => securityApi.attempts(hours),
  });
  const devices = useQuery({
    queryKey: ["security-devices"],
    queryFn: () => securityApi.devices(),
  });

  const saveSettings = useMutation({
    mutationFn: (patch: SecuritySettings) => securityApi.updateSettings(patch),
    onSuccess: () => {
      toast.success("Настройки безопасности сохранены");
      void queryClient.invalidateQueries({ queryKey: ["security-settings"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.detail : "Не удалось сохранить"),
  });

  const toggleTwofa = useMutation({
    mutationFn: (enabled: boolean) => securityApi.toggleTwofa(enabled),
    onSuccess: (result) => {
      if (user) setUser({ ...user, twofa_enabled: result.enabled });
      toast.success(
        result.enabled
          ? "Вход теперь подтверждается кодом с почты"
          : "Подтверждение входа отключено",
      );
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.detail : "Не удалось переключить"),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => securityApi.revokeDevice(id),
    onMutate: (id: string) => setRevokingId(id),
    onSettled: () => setRevokingId(null),
    onSuccess: () => {
      toast.success("Доверие устройству отозвано");
      void queryClient.invalidateQueries({ queryKey: ["security-devices"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.detail : "Не удалось отозвать"),
  });

  return (
    // p-6 как на остальных страницах: отступы задаёт страница, не layout.
    <div className="space-y-6 p-3 sm:p-6">
      <div>
        <h1 className="text-2xl font-semibold">Безопасность</h1>
        <p className="text-sm text-muted-foreground">
          Защита панели от подбора пароля и подтверждение входа кодом с почты.
        </p>
      </div>

      {settings.isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : settings.data ? (
        <SecuritySettingsCard
          settings={settings.data}
          saving={saveSettings.isPending}
          onSave={(patch) => saveSettings.mutate(patch)}
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Подтверждение входа для меня
          </CardTitle>
          <CardDescription>
            Код придёт на адрес из профиля. Если почта перестанет работать,
            подтверждение снимается командой на сервере — доступ не потеряется.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div className="pr-4">
              <p className="text-sm font-medium">
                Спрашивать код при входе под логином {user?.login ?? ""}
              </p>
              <p className="text-xs text-muted-foreground">
                Адрес для кода: {user?.email || "не задан — укажи в профиле"}
              </p>
            </div>
            <Switch
              checked={Boolean(user?.twofa_enabled)}
              disabled={toggleTwofa.isPending}
              onCheckedChange={(v) => toggleTwofa.mutate(Boolean(v))}
            />
          </div>
        </CardContent>
      </Card>

      <TrustedDevicesCard
        devices={devices.data ?? []}
        revokingId={revokingId}
        onRevoke={(id) => revoke.mutate(id)}
      />

      <LoginAttemptsCard
        attempts={attempts.data ?? []}
        hours={hours}
        onHoursChange={setHours}
      />
    </div>
  );
}
